import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useApp } from "../../../app/context/useApp";
import { getAllActiveSilos } from "../../../core/services/silos.service";
import {
  getConversation,
  getConversationBySilosId,
  deleteConversationById,
} from "../../../core/services/conversation.service";
import {
  fetchSchema,
  updateFileAttribute,
} from "../../../core/services/appService";
import { runQuery, getInsights } from "../../../core/services/analyze.service";
import { useLoadChatInsights } from "./useLoadChatInsights";
import { extractAnalyzeDisplayMeta } from "../../../core/utils/analyzeInsights";

const initialMessages = [
  {
    role: "assistant",
    text: "Hi — upload a file using the + button, then ask me to show a table, visualize a column, or query the data.",
  },
];

const parseSchemaData = (schemaData) => {
  const dataTypes =
    schemaData?.analyzeResponse?.success?.[0]?.Data_Types ||
    schemaData?.success?.[0]?.Data_Types;
  if (!dataTypes || typeof dataTypes !== "object") return [];

  return Object.entries(dataTypes).map(([name, type]) => ({
    name,
    type,
    originalName: name,
    detectedType: type,
  }));
};

const buildQueryMeta = (queryResponse, insights) => {
  const analyzePayload = queryResponse?.analyzeResponse || {};
  const successRows = analyzePayload?.success || queryResponse?.success || [];
  const displayMeta = extractAnalyzeDisplayMeta(queryResponse);

  return {
    preface: analyzePayload?.preface ?? queryResponse?.preface ?? "",
    title: analyzePayload?.Title ?? queryResponse?.Title ?? "",
    xAxis: analyzePayload?.["X-axis"] ?? queryResponse?.["X-axis"] ?? "",
    yAxis: analyzePayload?.["Y-axis"] ?? queryResponse?.["Y-axis"] ?? [],
    data: successRows,
    insights: insights ?? displayMeta.insights,
    faqs: displayMeta.faqs,
    charts:
      analyzePayload?.Possible_charts ??
      queryResponse?.Possible_charts ??
      [],
    table: successRows,
  };
};

const getSessionId = (session) =>
  session?.conversationsId ||
  session?.conversationId ||
  session?.ConversationId ||
  session?.ConversationsId ||
  session?.id ||
  null;

const isTemporaryConversationId = (conversationId) =>
  String(conversationId ?? "").startsWith("chat-");

const ANALYZE_FILE_NAME_CACHE_KEY = "analyze:fileNameByConversation";

const readAnalyzeFileNameCache = () => {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(ANALYZE_FILE_NAME_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeAnalyzeFileNameCache = (cache) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      ANALYZE_FILE_NAME_CACHE_KEY,
      JSON.stringify(cache || {}),
    );
  } catch {
    // Ignore storage failures (private mode/quota).
  }
};

const saveConversationFileName = (conversationId, resolvedFileName) => {
  const id = String(conversationId ?? "").trim();
  const name = String(resolvedFileName ?? "").trim();
  if (!id || !name || isTemporaryConversationId(id)) return;

  const current = readAnalyzeFileNameCache();
  writeAnalyzeFileNameCache({ ...current, [id]: name });
};

const getCachedConversationFileName = (conversationId) => {
  const id = String(conversationId ?? "").trim();
  if (!id) return "";
  const current = readAnalyzeFileNameCache();
  return String(current?.[id] || "").trim();
};

const extractConversationId = (payload) => {
  if (!payload) return null;

  const directId =
    payload?.conversationId ||
    payload?.conversationsId ||
    payload?.ConversationId ||
    payload?.ConversationsId ||
    payload?.id ||
    null;
  if (directId) return directId;

  if (payload?.analyzeResponse) {
    return extractConversationId(payload.analyzeResponse);
  }

  if (payload?.data) {
    return extractConversationId(payload.data);
  }

  if (Array.isArray(payload) && payload.length > 0) {
    return extractConversationId(payload[0]);
  }

  return null;
};

const extractMessageId = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  return (
    payload?.messagesId ||
    payload?.messageId ||
    payload?.MessagesId ||
    payload?.MessageId ||
    payload?.analyzeResponse?.messagesId ||
    payload?.analyzeResponse?.messageId ||
    payload?.analyzeResponse?.MessagesId ||
    payload?.analyzeResponse?.MessageId ||
    payload?.analyzeResponse?.id ||
    payload?.id ||
    null
  );
};

const normalizeAnalyzeMessage = (message) => {
  const role = String(message?.role || "").toLowerCase();
  const normalizedRole = role === "ai" ? "assistant" : role || "assistant";

  return {
    ...message,
    role: normalizedRole,
    text: message?.text ?? message?.displayContent ?? message?.content ?? "",
    meta: message?.meta ?? null,
  };
};

const resolveFileNameFromMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return "";

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const metaFileName =
      message?.meta?.fileName ||
      message?.meta?.filename ||
      message?.meta?.file_name ||
      message?.meta?.parquetFileName ||
      message?.meta?.parquet_file_name ||
      "";
    if (String(metaFileName).trim()) {
      return String(metaFileName).trim();
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const text = String(
      messages[index]?.text ??
        messages[index]?.displayContent ??
        messages[index]?.content ??
        "",
    );
    const uploadedFileMatch = text.match(/Uploaded file:\s*(.+)$/i);
    if (uploadedFileMatch?.[1]) {
      return uploadedFileMatch[1].trim();
    }
    const uploadedAssistantMatch = text.match(/File\s+"([^"]+)"\s+uploaded\.?/i);
    if (uploadedAssistantMatch?.[1]) {
      return uploadedAssistantMatch[1].trim();
    }
  }

  return "";
};

const normalizeFileNameVariants = (name) => {
  const raw = String(name ?? "").trim();
  if (!raw) return [];
  const withoutExtension = raw.replace(/\.[^/.]+$/, "").trim();
  const candidates = [raw];
  if (withoutExtension && withoutExtension !== raw) {
    candidates.push(withoutExtension);
  }
  return [...new Set(candidates)];
};

const FILE_NAME_LIKE_REGEX = /\.(csv|xlsx|xls|parquet)$/i;

const looksLikeFileName = (value) =>
  FILE_NAME_LIKE_REGEX.test(String(value ?? "").trim());

const resolveFileNameFromConversationSession = (session) => {
  if (!session || typeof session !== "object") return "";

  const directName =
    session?.fileName ??
    session?.filename ??
    session?.file_name ??
    session?.uploadedFileName ??
    session?.uploaded_file_name ??
    "";
  if (String(directName).trim()) {
    return String(directName).trim();
  }

  const titleBasedName =
    session?.title ??
    session?.conversationTitle ??
    session?.name ??
    "";
  if (looksLikeFileName(titleBasedName)) {
    return String(titleBasedName).trim();
  }

  const textProbe = String(
    session?.contentText ??
      session?.lastMessage ??
      session?.queryText ??
      session?.message ??
      "",
  );
  const uploadMatch =
    textProbe.match(/Uploaded file:\s*(.+)$/i) ||
    textProbe.match(/File\s+"([^"]+)"\s+uploaded\.?/i);
  if (uploadMatch?.[1]) return String(uploadMatch[1]).trim();

  return "";
};

const buildAnalyzeStateFromMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return {
      schemaColumns: null,
      resolvedFileName: "",
      hasAnalyzeContext: false,
      hasQueryResults: false,
      persistedConversationId: null,
    };
  }

  let schemaColumns = null;
  let hasQueryResults = false;
  let persistedConversationId = null;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const columns = message?.meta?.schema?.columns;
    if (!schemaColumns && Array.isArray(columns) && columns.length > 0) {
      schemaColumns = columns;
    }

    if (
      !hasQueryResults &&
      (Array.isArray(message?.meta?.data) || Array.isArray(message?.meta?.table))
    ) {
      hasQueryResults = true;
    }

    if (!persistedConversationId) {
      persistedConversationId =
        message?.conversationId ??
        message?.conversationsId ??
        message?.ConversationId ??
        message?.ConversationsId ??
        message?.meta?.userSessionFileId ??
        null;
    }

    if (schemaColumns && hasQueryResults && persistedConversationId) break;
  }

  const resolvedFileName = resolveFileNameFromMessages(messages);
  const hasAnalyzeContext =
    !!schemaColumns || !!resolvedFileName || !!persistedConversationId;

  return {
    schemaColumns,
    resolvedFileName,
    hasAnalyzeContext,
    hasQueryResults,
    persistedConversationId,
  };
};

export default function useAnalyzePage() {
  const {
    setActiveMode,
    selectedClient,
    selectedProject,
    messages: appMessages,
    setMessages: setAppMessages,
    currentChatSession,
    createNewChat,
    loadChatSession,
    renameChatSession,
    renamingConversationIds,
    isGenerating,
    showAlert,
  } = useApp();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatStage, setChatStage] = useState("welcome");
  const [isTyping, setIsTyping] = useState(false);
  const [isSchemaConfirmed, setIsSchemaConfirmed] = useState(false);
  const queryAbortControllerRef = useRef(null);
  const [analyzeSiloId, setAnalyzeSiloId] = useState(null);
  const [activeAnalyzeConversationId, setActiveAnalyzeConversationId] =
    useState(null);
  const [analyzeConversations, setAnalyzeConversations] = useState([]);
  const [loadingAnalyzeConversations, setLoadingAnalyzeConversations] =
    useState(false);
  const [deletingAnalyzeConversationIds, setDeletingAnalyzeConversationIds] =
    useState({});

  const userId = useMemo(() => selectedClient?.Id || 1, [selectedClient]);
  const selectedClientId = useMemo(
    () =>
      selectedClient?.clientId ??
      selectedClient?.Id ??
      selectedClient?.id ??
      null,
    [selectedClient],
  );

  useEffect(() => {
    return () => setActiveMode(null);
  }, [setActiveMode]);

  useEffect(() => {
    let active = true;

    const loadAnalyzeSiloId = async () => {
      try {
        const response = await getAllActiveSilos();
        const silos = Array.isArray(response?.data) ? response.data : [];
        const analyzeSilo = silos.find(
          (silo) => String(silo?.silosName || "").toLowerCase() === "analyze",
        );
        if (active) {
          setAnalyzeSiloId(analyzeSilo?.silosId ?? null);
        }
      } catch {
        if (active) {
          setAnalyzeSiloId(null);
        }
      }
    };

    loadAnalyzeSiloId();
    return () => {
      active = false;
    };
  }, []);

  const pushMessage = useCallback(
    (msg) => {
      setAppMessages((prev) => [...prev, msg]);
    },
    [setAppMessages],
  );

  const messages = useMemo(() => {
    const source = Array.isArray(appMessages) ? appMessages : [];
    if (!currentChatSession && source.length === 0) {
      return initialMessages;
    }
    if (source.length === 0) {
      return initialMessages;
    }
    return source.map(normalizeAnalyzeMessage);
  }, [appMessages, currentChatSession]);

  useLoadChatInsights(appMessages, setAppMessages);

  const resetChat = () => {
    setAppMessages([]);
    setFile(null);
    setFileName("");
    setSchema(null);
    setChatStage("welcome");
    setIsSchemaConfirmed(false);
  };

  const resolvedConversationId = useMemo(() => {
    const currentId = getSessionId(currentChatSession);
    if (currentId && !isTemporaryConversationId(currentId)) return currentId;
    if (
      activeAnalyzeConversationId &&
      !isTemporaryConversationId(activeAnalyzeConversationId)
    ) {
      return activeAnalyzeConversationId;
    }
    return null;
  }, [currentChatSession, activeAnalyzeConversationId]);

  const refreshAnalyzeConversations = useCallback(async () => {
    if (!analyzeSiloId) {
      setAnalyzeConversations([]);
      return;
    }

    setLoadingAnalyzeConversations(true);
    try {
      const projectId =
        selectedProject?.projectId ??
        selectedProject?.ProjectId ??
        selectedProject?.id ??
        null;
      const response = selectedClientId
        ? await getConversation(analyzeSiloId, {
            isGlobal: true,
            clientId: selectedClientId,
            projectId,
          })
        : await getConversationBySilosId(analyzeSiloId);
      const conversationData = Array.isArray(response?.data) ? response.data : [];
      conversationData.forEach((session) => {
        const sessionConversationId = getSessionId(session);
        const inferredFileName = resolveFileNameFromConversationSession(session);
        if (sessionConversationId && inferredFileName) {
          saveConversationFileName(sessionConversationId, inferredFileName);
        }
      });
      setAnalyzeConversations(
        conversationData.filter(
          (session) => !isTemporaryConversationId(getSessionId(session)),
        ),
      );
    } catch {
      setAnalyzeConversations([]);
    } finally {
      setLoadingAnalyzeConversations(false);
    }
  }, [analyzeSiloId, selectedClientId, selectedProject]);

  useEffect(() => {
    refreshAnalyzeConversations();
  }, [refreshAnalyzeConversations]);

  const handleFileUpload = async (rows, filename, fileObj) => {
    if (!fileObj) return;

    setFile(fileObj);
    setFileName(fileObj.name);
    setLoading(true);
    setChatStage("fileUploaded");
    pushMessage({ role: "assistant", text: ` ${filename} uploading....! ` });

    try {
      const siloId = analyzeSiloId;
      if (!siloId) {
        throw new Error("Analyze silo is not available.");
      }

      const conversationId = resolvedConversationId;
      const modelId =
        selectedProject?.modelId ??
        selectedProject?.ModelId ??
        1;
      let schemaData = await fetchSchema(fileObj, {
        silosId: siloId,
        conversationId,
        modelId,
        clientId: selectedClientId,
      });

      if (
        Array.isArray(schemaData) &&
        schemaData[0]?.includes?.("file is already present")
      ) {
        schemaData = await updateFileAttribute(fileObj, userId, conversationId);
      }

      const schemaArray = parseSchemaData(schemaData);
      let displayMeta = extractAnalyzeDisplayMeta(schemaData);
      const uploadMessageId = extractMessageId(schemaData);
      if (uploadMessageId) {
        try {
          const fetched = await getInsights(uploadMessageId);
          displayMeta = {
            insights: displayMeta.insights || fetched?.insights,
            faqs: displayMeta.faqs.length ? displayMeta.faqs : fetched?.faqs || [],
            parquetFileName:
              displayMeta.parquetFileName || fetched?.parquetFileName || "",
          };
        } catch {
          // Upload still succeeded; insights stay whatever the upload payload had.
        }
      }
      const resolvedUploadFileName =
        displayMeta.parquetFileName || fileObj.name;
      const persistedConversationId =
        extractConversationId(schemaData) || conversationId;
      if (persistedConversationId) {
        setActiveAnalyzeConversationId(persistedConversationId);
        saveConversationFileName(
          persistedConversationId,
          resolvedUploadFileName,
        );
      }
      setFileName(resolvedUploadFileName);
      setSchema(schemaArray);

      pushMessage({ role: "user", text: `Uploaded file: ${fileObj.name}` });
      pushMessage({
        role: "assistant",
        text: `File "${fileObj.name}" uploaded.`,
        id: uploadMessageId,
        messagesId: uploadMessageId,
        meta: {
          ...(schemaArray.length > 0 ? { schema: { columns: schemaArray } } : {}),
          userSessionFileId: persistedConversationId,
          fileName: resolvedUploadFileName,
          displayFileName: fileObj.name,
          insights: displayMeta.insights,
          faqs: displayMeta.faqs,
        },
      });
      await refreshAnalyzeConversations();
    } catch (err) {
      pushMessage({ role: "assistant", text: `Error: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSchemaUpdate = (updatedSchema) => {
    setSchema(updatedSchema.columns);
    setIsSchemaConfirmed(true);
    setChatStage("schemaConfirmed");
    pushMessage({
      role: "assistant",
      text: "Schema confirmed. Ask your question now.",
    });
  };

  const handleSendQuery = async (text) => {
    if (!text.trim() || isTyping) return;

    const activeConversationId =
      (isTemporaryConversationId(getSessionId(currentChatSession))
        ? null
        : getSessionId(currentChatSession)) ||
      (isTemporaryConversationId(activeAnalyzeConversationId)
        ? null
        : activeAnalyzeConversationId) ||
      resolvedConversationId;

    setLoading(true);
    pushMessage({ role: "user", text });
    setIsTyping(true);
    const controller = new AbortController();
    queryAbortControllerRef.current = controller;

    try {
      const modelId =
        selectedProject?.modelId ??
        selectedProject?.ModelId ??
        1;
      const runtimeConversationId = activeConversationId || resolvedConversationId;
      const restoredFileName =
        String(fileName || "").trim() ||
        getCachedConversationFileName(runtimeConversationId) ||
        resolveFileNameFromConversationSession(currentChatSession);
      if (!String(fileName || "").trim()) {
        setFileName(restoredFileName);
      }

      const sessionLevelFileName = resolveFileNameFromConversationSession(currentChatSession);
      const fileNameCandidates = [
        ...normalizeFileNameVariants(restoredFileName),
        ...normalizeFileNameVariants(sessionLevelFileName),
      ].filter(Boolean);
      const uniqueCandidates = [...new Set(fileNameCandidates)];
      if (runtimeConversationId && fileNameCandidates.length === 0) {
        // Legacy sessions may rely only on conversationId server-side.
        uniqueCandidates.push("");
      }
      let queryResponse = null;
      let lastRunQueryError = null;

      for (const candidateFileName of uniqueCandidates) {
        try {
          queryResponse = await runQuery({
            file_name: candidateFileName,
            question: text,
            conversationId: runtimeConversationId,
            modelId,
          });
          setFileName(candidateFileName);
          break;
        } catch (candidateError) {
          lastRunQueryError = candidateError;
        }
      }

      if (!queryResponse) {
        if (runtimeConversationId && uniqueCandidates.length > 0) {
          pushMessage({
            role: "assistant",
            text: "I could not restore a file name for this old chat. I tried continuing with conversation history, but the backend rejected it. Please upload the same file once to relink this conversation.",
          });
          return;
        }
        throw lastRunQueryError || new Error("Unable to fetch analyze response.");
      }
      const persistedConversationId =
        extractConversationId(queryResponse) || activeConversationId;
      if (persistedConversationId) {
        setActiveAnalyzeConversationId(persistedConversationId);
        saveConversationFileName(
          persistedConversationId,
          String(fileName || "").trim() || restoredFileName,
        );
      }
      const hasAnalyzeRows = Array.isArray(
        queryResponse?.analyzeResponse?.success ?? queryResponse?.success,
      );
      const displayMeta = extractAnalyzeDisplayMeta(queryResponse);
      const hasDisplayPayload =
        hasAnalyzeRows ||
        !!displayMeta.insights ||
        displayMeta.faqs.length > 0;
      if (!hasDisplayPayload) {
        pushMessage({
          role: "assistant",
          text: "I didn’t get a proper response. Try asking differently.",
        });
        return;
      }

      const messageId = extractMessageId(queryResponse);
      let insights = displayMeta.insights;
      let faqs = displayMeta.faqs;
      if (messageId) {
        try {
          const insightPayload = await getInsights(messageId);
          insights = insightPayload?.insights || insights;
          if (insightPayload?.faqs?.length) {
            faqs = insightPayload.faqs;
          }
        } catch {
          // Keep insights/faqs from the query payload when getInsights fails.
        }
      }
      const meta = {
        ...buildQueryMeta(queryResponse, insights),
        faqs,
      };

      pushMessage({
        role: "assistant",
        text: meta.title || "Query Results",
        meta,
      });
      await refreshAnalyzeConversations();
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      const serverErrorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : "") ||
        "";
      pushMessage({
        role: "assistant",
        text: `Sorry, something went wrong while fetching the data.${
          serverErrorMessage ? ` (${serverErrorMessage})` : ""
        }`,
      });
    } finally {
      queryAbortControllerRef.current = null;
      setLoading(false);
      setIsTyping(false);
    }
  };

  const handleNewChat = useCallback(() => {
    resetChat();
    setActiveAnalyzeConversationId(null);
    createNewChat(selectedClient);
    refreshAnalyzeConversations();
  }, [createNewChat, selectedClient, refreshAnalyzeConversations]);

  const handleSelectConversation = useCallback(
    async (session) => {
      if (!session) return;
      resetChat();
      const selectedConversationId = getSessionId(session);
      setActiveAnalyzeConversationId(selectedConversationId);
      const sessionLevelFileName = resolveFileNameFromConversationSession(session);
      if (sessionLevelFileName) {
        setFileName(sessionLevelFileName);
        setFile({ name: sessionLevelFileName });
        if (selectedConversationId) {
          saveConversationFileName(selectedConversationId, sessionLevelFileName);
        }
      }
      await loadChatSession(session);
    },
    [loadChatSession],
  );

  useEffect(() => {
    const sourceMessages = Array.isArray(appMessages) ? appMessages : [];
    if (sourceMessages.length === 0) return;

    const {
      schemaColumns,
      resolvedFileName,
      hasAnalyzeContext,
      hasQueryResults,
      persistedConversationId,
    } = buildAnalyzeStateFromMessages(sourceMessages);

    if (!hasAnalyzeContext) return;

    if (Array.isArray(schemaColumns) && schemaColumns.length > 0) {
      setSchema(schemaColumns);
      setIsSchemaConfirmed(true);
    }

    if (resolvedFileName) {
      setFileName(resolvedFileName);
    }

    // Mark file as available so previously uploaded sessions can continue querying.
    setFile((prev) => prev || { name: resolvedFileName || "uploaded-file" });

    if (persistedConversationId && !isTemporaryConversationId(persistedConversationId)) {
      setActiveAnalyzeConversationId(persistedConversationId);
    }

    if (!resolvedFileName && persistedConversationId) {
      const cachedFileName = getCachedConversationFileName(persistedConversationId);
      if (cachedFileName) {
        setFileName(cachedFileName);
      }
    } else if (resolvedFileName && persistedConversationId) {
      saveConversationFileName(persistedConversationId, resolvedFileName);
    }

    setChatStage(hasQueryResults ? "schemaConfirmed" : "fileUploaded");
  }, [appMessages]);

  const handleRenameConversation = useCallback(
    async (session, newTitle) => {
      const ok = await renameChatSession(session, newTitle);
      if (ok) await refreshAnalyzeConversations();
      return ok;
    },
    [renameChatSession, refreshAnalyzeConversations],
  );

  const handleDeleteConversation = useCallback(
    async (session) => {
      const conversationId = getSessionId(session);
      if (!conversationId || isTemporaryConversationId(conversationId)) return;

      const deleteKey = String(conversationId);
      setDeletingAnalyzeConversationIds((prev) => ({ ...prev, [deleteKey]: true }));
      try {
        await deleteConversationById(conversationId);
        if (String(activeAnalyzeConversationId ?? "") === deleteKey) {
          resetChat();
          setActiveAnalyzeConversationId(null);
        }
        await refreshAnalyzeConversations();
      } finally {
        setDeletingAnalyzeConversationIds((prev) => {
          const next = { ...prev };
          delete next[deleteKey];
          return next;
        });
      }
    },
    [activeAnalyzeConversationId, refreshAnalyzeConversations],
  );

  const sortedAnalyzeConversations = useMemo(() => {
    if (!Array.isArray(analyzeConversations)) return [];
    return [...analyzeConversations].sort(
      (a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date),
    );
  }, [analyzeConversations]);

  return {
    file,
    fileName,
    schema,
    loading,
    messages,
    conversations: sortedAnalyzeConversations,
    currentChatSessionId:
      resolvedConversationId || getSessionId(currentChatSession),
    deletingConversationIds: deletingAnalyzeConversationIds,
    renamingConversationIds,
    loadingConversations: loadingAnalyzeConversations,
    chatStage,
    isTyping,
    isSchemaConfirmed,
    isGenerating,
    showAlert,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleRenameConversation,
    resetChat,
    handleFileUpload,
    handleSchemaUpdate,
    handleSendQuery,
    // handleStopQuery,
  };
}
